import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Database,
  ArrowRightLeft,
  Shield,
  CheckCircle, 
  ArrowRight,
  Cloud,
  Server,
  Workflow,
  FileText,
  Settings,
  Clock,
  Target,
  Users,
  TrendingUp,
  Zap,
  Globe,
  Lock,
  RefreshCw,
  Monitor,
  BarChart3,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  HardDrive
} from 'lucide-react';

const DataMigrationIntegration: React.FC = () => {
  const migrationServices = [
    {
      icon: <Database className="w-6 h-6" />,
      title: "Legacy System Migration",
      description: "Seamlessly migrate data from legacy CRM, ERP, and business systems to modern Microsoft platforms.",
      features: ["Data assessment", "Migration planning", "Zero downtime migration", "Data validation"]
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Migration",
      description: "Move your on-premises data and applications to Microsoft Azure and Office 365 cloud platforms.",
      features: ["Cloud readiness assessment", "Hybrid migration", "Security compliance", "Performance optimization"]
    },
    {
      icon: <Server className="w-6 h-6" />,
      title: "Database Migration",
      description: "Migrate databases from various platforms to SQL Server, Azure SQL, or Cosmos DB.",
      features: ["Schema conversion", "Data transformation", "Performance tuning", "Backup & recovery"]
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Document Migration",
      description: "Transfer documents and files to SharePoint Online with proper metadata and structure.",
      features: ["Metadata mapping", "Version history", "Permission migration", "Content organization"]
    }
  ];

  const integrationServices = [
    {
      icon: <ArrowRightLeft className="w-6 h-6" />,
      title: "System Integration",
      description: "Connect Microsoft Dynamics 365 with third-party applications and legacy systems.",
      features: ["API integration", "Real-time sync", "Custom connectors", "Error handling"]
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: "Process Automation",
      description: "Automate data flows and business processes across integrated systems.",
      features: ["Power Automate flows", "Business logic", "Approval workflows", "Scheduled processes"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Data Analytics Integration",
      description: "Integrate business intelligence and reporting tools with your data sources.",
      features: ["Power BI integration", "Data warehousing", "ETL processes", "Real-time dashboards"]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Platform Integration",
      description: "Connect Microsoft solutions with Salesforce, Oracle, SAP, and other enterprise platforms.",
      features: ["Cross-platform sync", "Data mapping", "Conflict resolution", "Monitoring & alerts"]
    }
  ];

  const migrationProcess = [
    {
      step: "01",
      title: "Data Assessment",
      description: "Comprehensive analysis of your existing data sources, quality, and migration requirements.",
      color: "bg-blue-500"
    },
    {
      step: "02",
      title: "Migration Planning",
      description: "Develop detailed migration strategy including timelines, resources, and risk mitigation.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "Data Preparation",
      description: "Clean, transform, and prepare data for migration with quality validation.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "Migration Execution",
      description: "Execute migration with minimal downtime using proven tools and methodologies.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Testing & Validation",
      description: "Comprehensive testing to ensure data integrity and system functionality.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Go-Live & Support",
      description: "Launch migrated systems with ongoing monitoring and support services.",
      color: "bg-indigo-500"
    }
  ];

  const integrationPatterns = [
    {
      title: "Real-Time Integration",
      description: "Immediate data synchronization between systems using APIs and webhooks.",
      icon: <Zap className="w-8 h-8" />
    },
    {
      title: "Batch Integration",
      description: "Scheduled data transfers for large volumes or non-critical updates.",
      icon: <Clock className="w-8 h-8" />
    },
    {
      title: "Event-Driven Integration",
      description: "Trigger-based data flows responding to business events and changes.",
      icon: <GitBranch className="w-8 h-8" />
    },
    {
      title: "Hybrid Integration",
      description: "Combination of cloud and on-premises integration for complex scenarios.",
      icon: <Cloud className="w-8 h-8" />
    }
  ];

  const benefits = [
    "Eliminate data silos and improve accessibility",
    "Reduce manual data entry by 90%",
    "Ensure data consistency across all systems",
    "Improve decision-making with unified data",
    "Enhance productivity through automation",
    "Maintain data security and compliance"
  ];

  const technologies = [
    "Microsoft Azure Data Factory",
    "SQL Server Integration Services (SSIS)",
    "Power Platform (Power Automate)",
    "Azure Logic Apps",
    "Microsoft Graph API",
    "REST and SOAP APIs",
    "Custom .NET Applications",
    "Third-party Integration Tools"
  ];

  const challengesSolutions = [
    {
      challenge: "Data Quality Issues",
      solution: "Comprehensive data cleansing and validation processes",
      icon: <CheckSquare className="w-6 h-6" />
    },
    {
      challenge: "System Downtime",
      solution: "Zero-downtime migration strategies and rollback plans",
      icon: <RefreshCw className="w-6 h-6" />
    },
    {
      challenge: "Security Concerns",
      solution: "End-to-end encryption and compliance with industry standards",
      icon: <Shield className="w-6 h-6" />
    },
    {
      challenge: "Complex Data Relationships",
      solution: "Advanced mapping and transformation tools",
      icon: <GitBranch className="w-6 h-6" />
    }
  ];

  return (
    <>
      <SEOHead
        title="Data Migration & Integration Services | CRMONCE - Expert Data Solutions"
        description="Professional data migration and system integration services for Microsoft Dynamics 365, Azure, and Office 365. Seamless data transfer with zero downtime and complete security."
        keywords="data migration, system integration, Microsoft Dynamics 365 migration, cloud migration, database migration, legacy system migration, API integration, data transformation"
        canonical="https://crmonce.com/data-migration-integration"
        ogImage="/images/data-migration-integration-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Data Migration & Integration Services",
          "description": "Professional data migration and system integration services for Microsoft platforms. Expert data transfer, transformation, and integration solutions.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "Data Migration & Integration",
          "category": "Data Services",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Data Migration & Integration Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Legacy System Migration",
                  "description": "Migrate data from legacy systems to modern Microsoft platforms"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Migration",
                  "description": "Move on-premises data to Microsoft Azure and Office 365"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "System Integration",
                  "description": "Connect Microsoft Dynamics 365 with third-party applications"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <Database className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Data Migration & Integration</h1>
              </div>
              <p className="text-xl text-blue-100 mb-8">
                Seamlessly migrate your data and integrate systems with Microsoft platforms. 
                Expert data transformation, zero-downtime migrations, and comprehensive integration solutions.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start Your Migration Journey
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Migration Services */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Data Migration Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional data migration services to move your business-critical data 
                safely and efficiently to modern Microsoft platforms.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {migrationServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Services */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                System Integration Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Connect your business systems and create unified data flows that 
                enhance productivity and decision-making across your organization.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {integrationServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-purple-600 mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Migration Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We follow a proven methodology to ensure successful data migration 
                with minimal risk and maximum efficiency.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {migrationProcess.map((process, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${process.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Patterns */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Integration Patterns
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the right integration approach based on your business requirements 
                and technical constraints.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {integrationPatterns.map((pattern, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="text-blue-600 mb-4 flex justify-center">
                    {pattern.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{pattern.title}</h3>
                  <p className="text-gray-600 text-sm">{pattern.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Common Challenges & Our Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We address the most common data migration and integration challenges 
                with proven solutions and best practices.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {challengesSolutions.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-red-500 mt-1">
                      <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Challenge: {item.challenge}
                      </h3>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 mt-4">
                    <div className="text-green-500 mt-1">
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-green-600 mb-2">Solution:</h4>
                      <p className="text-gray-600">{item.solution}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Technologies & Tools
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We leverage cutting-edge Microsoft technologies and industry-leading 
                tools for reliable data migration and integration.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                    <HardDrive className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-gray-700 font-medium">{tech}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Our Services?
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Transform your data landscape with professional migration and integration 
                services that deliver measurable business value.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Migrate Your Data?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our data migration experts help you move to modern Microsoft platforms 
                with confidence, security, and zero data loss.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Migration Assessment
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  View Case Studies
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DataMigrationIntegration;
